import React from 'react';
import { useFormContext, Controller, Control } from 'react-hook-form';
import { CollaboratorFormContext } from "@/features/Collaborators/Edit/Contexts/CollaboratorFormContext";
import { RequiredIndicator } from '@/components/shared';
import { collaboratorSchemaHelpers } from '@/features/Collaborators/Models/collaborator';

interface GiftsAndPreferencesFieldsetProps {
    context: CollaboratorFormContext;
    control?: Control<any>;
}

const GiftsAndPreferencesFieldset: React.FC<GiftsAndPreferencesFieldsetProps> = ({ context, control: propControl }) => {
    // Sempre chamar o hook para cumprir a regra de Hooks;
    // se não houver FormProvider disponível, ctx.control será undefined.
    const ctx = useFormContext();

    const control: Control<any> | undefined = propControl ?? ctx?.control;

    if (!control) {
        throw new Error('GiftsAndPreferencesFieldset requires either a control prop or to be inside a FormProvider.');
    }

    // Helper para verificar se campo deve aparecer
    const shouldShow = (fieldName: string) => collaboratorSchemaHelpers.shouldShowField(fieldName, context);

    // Helper para verificar se campo é obrigatório
    const isRequired = (field: string) => collaboratorSchemaHelpers.isFieldRequired(field, context);

    // Early return se não deve mostrar neste contexto
    if (!shouldShow('shirtSize') && !shouldShow('hasOnboardingKit') && !shouldShow('generalObservations')) {
        return null;
    }

    // Opções de tamanho de camiseta
    const shirtSizeOptions = [
        { value: '', label: 'Selecione...' },
        { value: 'P', label: 'P' },
        { value: 'M', label: 'M' },
        { value: 'G', label: 'G' },
        { value: 'GG', label: 'GG' },
        { value: 'GGG', label: 'GGG' },
        { value: 'GGGG', label: 'GGGG' },
        { value: 'Outro', label: 'Outro' }
    ];

    // Perguntas de interesse
    const questions = [
        { name: 'freeTimeActivities', label: 'O que você gosta de fazer no tempo livre?' },
        { name: 'favoriteFoodAndDrink', label: 'Qual sua comida e bebida preferida?' },
        { name: 'favoriteColor', label: 'Qual sua cor favorita?' },
        { name: 'favoriteBook', label: 'Possui algum livro favorito?' },
        { name: 'favoriteMovieOrSeries', label: 'Qual seu filme/série favorita?' },
        { name: 'favoriteCharacter', label: 'Qual personagem favorito?' },
        { name: 'sportsPractice', label: 'Pratica algum esporte? Se sim, qual?' },
        { name: 'specialTalent', label: 'Você tem algum talento especial que gostaria de compartilhar com a gente?' },
        { name: 'futureGoals', label: 'Você tem alguma meta ou sonho que gostaria de alcançar no futuro?' },
        { name: 'generalObservations', label: 'Observações gerais' }
    ];

    return (
        <fieldset data-testid="gifts-and-preferences-fieldset" className="border p-4 rounded-md">
            <legend className="text-sm font-medium px-1 text-gray-400">Interesses Pessoais e Brindes</legend>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 pt-2">

                {/* Tamanho de Camiseta */}
                {shouldShow('shirtSize') && (
                    <div className="md:col-span-1">
                        <label htmlFor="shirtSize" className="block text-sm font-medium text-gray-700">
                            Tamanho de Camiseta {isRequired('shirtSize') && <RequiredIndicator />}
                        </label>
                        <Controller
                            name="shirtSize"
                            control={control}
                            rules={{ required: isRequired('shirtSize') ? 'Tamanho de camiseta é obrigatório' : false }}
                            render={({ field, fieldState }) => (
                                <>
                                    <select
                                        {...field}
                                        id="shirtSize"
                                        className="input-primary w-full mt-1"
                                    >
                                        {shirtSizeOptions.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                    {fieldState.error && (
                                        <span className="text-red-500 text-sm mt-1">{fieldState.error.message}</span>
                                    )}
                                </>
                            )}
                        />
                    </div>
                )}

                {/* Tamanho de Chinelo */}
                {shouldShow('slipperSize') && (
                    <div className="md:col-span-1">
                        <label htmlFor="slipperSize" className="block text-sm font-medium text-gray-700">
                            Tamanho de Chinelo {isRequired('slipperSize') && <RequiredIndicator />}
                        </label>
                        <Controller
                            name="slipperSize"
                            control={control}
                            rules={{ required: isRequired('slipperSize') ? 'Tamanho de chinelo é obrigatório' : false }}
                            render={({ field, fieldState }) => (
                                <>
                                    <input
                                        {...field}
                                        type="text"
                                        id="slipperSize"
                                        className="input-primary w-full mt-1"
                                        placeholder="Ex: 42"
                                    />
                                    {fieldState.error && (
                                        <span className="text-red-500 text-sm mt-1">{fieldState.error.message}</span>
                                    )}
                                </>
                            )}
                        />
                    </div>
                )}

                {/* Checkboxes de Kits */}
                {(shouldShow('hasOnboardingKit') || shouldShow('hasBackpack')) && (
                    <div className="md:col-span-2 grid grid-cols-2 gap-x-6">
                        {shouldShow('hasOnboardingKit') && (
                            <div className="flex items-center">
                                <Controller
                                    name="hasOnboardingKit"
                                    control={control}
                                    render={({ field }) => (
                                        <input
                                            type="checkbox"
                                            id="hasOnboardingKit"
                                            checked={field.value || false}
                                            onChange={(e) => field.onChange(e.target.checked)}
                                            className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                        />
                                    )}
                                />
                                <label htmlFor="hasOnboardingKit" className="ml-2 block text-sm text-gray-900">
                                    Recebeu Kit Onboarding
                                </label>
                            </div>
                        )}

                        {shouldShow('hasBackpack') && (
                            <div className="flex items-center">
                                <Controller
                                    name="hasBackpack"
                                    control={control}
                                    render={({ field }) => (
                                        <input
                                            type="checkbox"
                                            id="hasBackpack"
                                            checked={field.value || false}
                                            onChange={(e) => field.onChange(e.target.checked)}
                                            className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                        />
                                    )}
                                />
                                <label htmlFor="hasBackpack" className="ml-2 block text-sm text-gray-900">
                                    Recebeu Mochila
                                </label>
                            </div>
                        )}
                    </div>
                )}

                {/* Perguntas de Interesse */}
                {questions.map((question) => (
                    shouldShow(question.name) && (
                        <div className="md:col-span-2" key={question.name}>
                            <label htmlFor={question.name} className="block text-sm font-medium text-gray-700">
                                {question.label} {isRequired(question.name) && <RequiredIndicator />}
                            </label>
                            <Controller
                                name={question.name}
                                control={control}
                                rules={{ required: isRequired(question.name) ? `${question.label} é obrigatório` : false }}
                                render={({ field, fieldState }) => (
                                    <>
                                        <textarea
                                            {...field}
                                            id={question.name}
                                            className="input-primary w-full mt-1"
                                            rows={2}
                                            placeholder={`Digite sua resposta...`}
                                        />
                                        {fieldState.error && (
                                            <span className="text-red-500 text-sm mt-1">{fieldState.error.message}</span>
                                        )}
                                    </>
                                )}
                            />
                        </div>
                    )
                ))}
            </div>
        </fieldset>
    );
};

export default GiftsAndPreferencesFieldset; 